/**
 * Stale-while-revalidate, written once.
 *
 * A list pane may show a loading placeholder INSTEAD of content only before
 * its first successful read. Every later refetch — push event, poll, manual
 * refresh — keeps what is on screen and swaps the data in when it lands.
 *
 * Why this is a module and not a convention: `useWaitingQueue` (correctly)
 * raises `loading` on every refetch, and forwarding that flag straight into
 * `WaitingQueue` made the add-in pane swap "Needs you · 0 / Nothing waiting on
 * you" for "Checking what needs you…" every 30 s and on every push — a 440 px
 * strip that never sits still. Ready briefs never did that, because its
 * loading flag was cleared once and never raised again. This is that rule,
 * shared by both, so the next list pane does not rediscover the difference.
 *
 * Usage: `loading={isInitialLoading({ ready, lastSuccessAt, lastError })}`.
 */
export interface InitialLoadingArgs {
  /** Timestamp of the last SUCCESSFUL read, or `null` when none has landed. */
  lastSuccessAt: number | null;
  /** The most recent read's error, if it failed. A first read that failed is
   *  not "still loading": the caller's stale/error branch owns that state and
   *  must not be hidden behind a spinner. */
  lastError?: unknown;
  /** `false` while a precondition (mailbox scope, auth, …) is still resolving;
   *  treated as "before the first read". Defaults to `true`. */
  ready?: boolean;
}

export function isInitialLoading({
  lastSuccessAt,
  lastError = null,
  ready = true,
}: InitialLoadingArgs): boolean {
  if (!ready) return true;
  return lastSuccessAt === null && !lastError;
}
